import { useApp } from "@/store/app";
import { wait } from "@/utils";
import Xmpp from "../Xmpp";
import CrashReporter from "./CrashReporter";

const START_ATTEMPTS = 3;
const START_RETRY_DELAY = 2000;

export default function createNetworkManager() {
  let xmpp: Xmpp | null = null;
  let timeoutId: NodeJS.Timeout | null = null;
  const setStatus = useApp.getState().setStatus;

  const clearScheduledReconnect = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
  };

  const reconnect = async () => {
    clearScheduledReconnect();
    log.app("Reconnecting...");
    try {
      await xmpp?.reconnect();
      setStatus("STARTED");
    } catch (err) {
      log.app("Error while restarting xmpp", err);
      clearScheduledReconnect();
      timeoutId = setTimeout(() => reconnect(), 5000);
    }
  };

  const isAppReady = () => {
    const status = useApp.getState().status;
    const isReady = status !== "STARTING" && status !== "FAILURE" && status !== "ALREADY_RUNNING";
    if (!isReady) log.app("App has not started, ignoring network event");
    return isReady;
  };

  const onOnline = () => {
    log.app("Network available");
    if (!isAppReady() || xmpp?.isConnected()) return;
    reconnect();
  };

  const onOffline = () => {
    log.app("Network unavailable");
    if (!isAppReady()) return;
    setStatus("NO_NETWORK");
  };

  const handleStart = async (xmppCredentials: XmppCredentials) => {
    xmpp = Xmpp.getInstance();
    xmpp.setCredentials(xmppCredentials);
    for (let attempt = 1; attempt <= START_ATTEMPTS; attempt++) {
      try {
        await xmpp.connect();
        xmpp.pingService.start();
        xmpp.sendPresence("not_chosen_yet");
        if (attempt > 1) CrashReporter.sendMessage(`NetworkManager 'handleStart' succeeded on attempt ${attempt}`);
        return;
      } catch (err) {
        log.err(`Error while starting app (attempt ${attempt}/${START_ATTEMPTS})`, err);
        try {
          await xmpp.disconnect();
        } catch (err) {
          log.app("Couldn't disconnect from xmpp on app failure", err);
        }
        const isLastAttempt = attempt === START_ATTEMPTS;
        if (isLastAttempt) throw Error("NetworkManager 'handleStart' failed", { cause: err });
        await wait(attempt * START_RETRY_DELAY);
      }
    }
  };

  function startListening() {
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
  }

  function stopListening() {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
  }

  return {
    start: handleStart,
    reconnect,
    startListening,
    stopListening
  };
}
